<template>
  <v-dialog v-model="dialog" max-width="800px" :fullscreen="$vuetify.breakpoint.mdAndDown || dialogFullScreen" :hide-overlay="$vuetify.breakpoint.mdAndDown" scrollable no-click-animation>
    <v-card>
      <v-toolbar dark color="primary" :max-height="$vuetify.breakpoint.mdAndUp ? 64 : 56">
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ $t('components.export_dialog.title') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn v-if="$vuetify.breakpoint.mdAndUp" icon @click="dialogFullScreen = !dialogFullScreen">
            <v-icon v-if="dialogFullScreen === true">mdi-fullscreen-exit</v-icon>
            <v-icon v-else>mdi-fullscreen</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <div class="subtitle-2 mt-5">{{ $t('components.export_dialog.help') }}</div>
        <v-form ref="form" v-model="valid" lazy-validation class="mt-3" @submit.prevent.stop>
          <global-filters ref="globalFilters" :dialogFullScreen="dialogFullScreen" classes="mx-0"/>
          <div class="v-text-field__details"><p v-if="noData" class="v-messages__message error--text" style="font-size: 12px;padding-left: 12px;padding-top: 5px;">Pas de données</p></div>
          <div class="subtitle-2 mt-5">{{ $t('components.export_dialog.prefix') }}</div>
          <v-text-field
            v-model="prefix"
            :rules="activePrefixRules"
            counter="15"
            outlined
            class="mt-3"
            dense
            :hint="$t('components.export_dialog.prefix_hint')"
            persistent-hint
            @keydown.enter.stop.prevent
          ></v-text-field>
        </v-form>

        <v-btn
          color="primary"
          class="mt-5"
          :disabled="!canSubmit || longRunning"
          :loading="loading"
          @click="submitExport"
        >
          {{ $t('components.export_dialog.request_export') }}
        </v-btn>

        <div class="text-caption grey--text text--darken-1 mt-2">
          Vos fichiers prêts à télécharger s'afficheront ci-dessous. Si votre requête est volumineuse, elle peut prendre un peu de temps. Vous pouvez fermer cette fenêtre et revenir télécharger votre fichier plus tard.
        </div>

        <div v-if="longRunning" class="mt-4">
          <v-alert dense outlined type="info" class="mb-2" color="primary">
            Votre demande d'export est en cours de traitement en arrière-plan. La liste ci-dessous se mettra à jour automatiquement dès qu'un fichier sera prêt.
          </v-alert>
          <v-progress-linear color="primary" indeterminate height="4"></v-progress-linear>
        </div>

        <v-divider class="my-5"></v-divider>
        <div class="subtitle-2 mb-2">{{ $t('components.export_dialog.your_exports') }}</div>
        <v-skeleton-loader v-if="loadingList" type="list-item-two-line@3"></v-skeleton-loader>
        <v-alert v-else-if="!exports.length" dense outlined type="info">{{ $t('components.export_dialog.no_exports') }}</v-alert>
        <v-list v-else dense>
          <template v-for="(exp, idx) in exports">
            <v-list-item dense :key="`item-${idx}`" :class="{ 'v-list-item--active': !isDownloaded(exp) }">
              <v-list-item-avatar size="32"><v-icon>mdi-file</v-icon></v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  <span>{{ exp.filename }}</span>
                </v-list-item-title>
<!--              <v-list-item-subtitle>{{ exp.last_modified }}</v-list-item-subtitle>-->
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon :href="exp.url" target="_blank" @click.prevent="download(exp)">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="idx < exports.length - 1" :key="`div-${idx}`"></v-divider>
          </template>
        </v-list>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import GlobalFiltersStore from '@/stores/GlobalFiltersStore'
import AuthLogic from '@/logics/AuthLogic'
import AlixLogic from '@/logics/AlixLogic'
import GlobalFilters from '@/components/communs/GlobalFilters.vue'
import { EventBus } from '@/services/Request'
import ModelLogic from '@/logics/ModelLogic'

export default {
  name: 'ExportDialog',
  components: { GlobalFilters },
  data: () => ({
    dialog: false,
    dialogFullScreen: false,
    valid: false,
    prefix: '',
    loading: false,
    loadingList: false,
    noData: false,
    exports: [],
    gfTick: 0,
    prefixValidateNow: false,
    // Long-running handling
    longRunning: false,
    uiTimeoutMs: 12000, // soft UI timeout to inform user
    polling: false,
    pollIntervalMs: 5000,
    pollMaxAttempts: 24, // ~2 minutes
    pollAttempts: 0,
    _pollHandle: null,
    _uiTimeoutHandle: null,
    // Download tracking via user metas
    downloadedExports: [], // array of basenames already downloaded by the user
    downloadedMetaKey: 'downloaded_exports',
    prefixRules: [
      v => !!v || 'Prefixe requis',
      v => (v && v.length <= 15) || 'Max 15 caractères',
      v => (/^[A-Za-z0-9_-]+$/).test(v || '') || 'Caractères autorisés: lettres, chiffres, -, _'
    ]
  }),
  computed: {
    canSubmit() {
      // Tie reactivity to global filters updates via gfTick
      const _tick = this.gfTick
      return GlobalFiltersStore.selectedUnits.length && GlobalFiltersStore.startDate && GlobalFiltersStore.endDate
    },
    activePrefixRules() {
      return this.prefixValidateNow ? this.prefixRules : []
    }
  },
  mounted() {
    // Listen to global filters updates to refresh canSubmit reactivity
    this._gfListener = () => { this.gfTick++; this.noData = false }
    EventBus.$on('update-global-filters', this._gfListener)
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.stopPolling()
        this.clearUiTimeout()
        this.longRunning = false
      }
    }
  },
  beforeDestroy() {
    if (this._gfListener) {
      EventBus.$off('update-global-filters', this._gfListener)
      this._gfListener = null
    }
    this.stopPolling()
    this.clearUiTimeout()
  },
  methods: {
    async openDialog() {
      this.dialog = true
      await this.$nextTick()
      // Load user's downloaded exports from metas
      this.loadDownloadedFromMetas()
      await this.fetchExports();
    },
    async fetchExports() {
      this.loadingList = true
      try {
        const user = AuthLogic.user()
        const res = await AlixLogic.listUserExports(user.uuid)
        this.exports = res.data || res || []
      } catch (e) {
        // Show error to user
        const msg = (e && (e.message || e.msg)) || (e && e.response && (e.response.data && (e.response.data.message || e.response.data.error))) || ''
        const text = this.$t('components.export_dialog.fetch_exports_failed') + (msg ? `: ${msg}` : '')
        EventBus.$emit('show-snackbar', { text, color: 'red', timeout: 4000 })
        this.exports = []
      } finally {
        this.loadingList = false
      }
    },
    loadDownloadedFromMetas() {
      try {
        const user = AuthLogic.user() || {}
        const metas = Array.isArray(user.metas) ? user.metas : []
        const m = metas.find(mm => mm && mm.key === this.downloadedMetaKey)
        let val = m ? m.value : []
        if (!Array.isArray(val)) {
          if (typeof val === 'string') {
            try { val = JSON.parse(val) } catch (e) { val = [] }
          } else {
            val = []
          }
        }
        // Deduplicate and keep strings only
        this.downloadedExports = Array.from(new Set(val.filter(v => typeof v === 'string')))
      } catch (e) {
        this.downloadedExports = []
      }
    },
    getBasename(exp) {
      const s = (exp && (exp.key || exp.filename || '')) + ''
      return s.split('/').pop()
    },
    isDownloaded(exp) {
      const name = this.getBasename(exp)
      return this.downloadedExports.indexOf(name) !== -1
    },
    async persistDownloadedMetas() {
      try {
        const user = AuthLogic.user()
        // Limit growth: keep last 500
        if (this.downloadedExports.length > 500) {
          this.downloadedExports = this.downloadedExports.slice(-500)
        }
        await ModelLogic.updateOne('users', user.id + '/metas', {
          key: this.downloadedMetaKey,
          value: this.downloadedExports
        })
        // Optimistically refresh user metas in background
        AuthLogic.loadUser()
      } catch (e) {
        // Non-blocking failure; log only
        console.warn('Failed to persist downloaded_exports meta', e)
      }
    },
    async markAsDownloaded(expOrName, persist = true) {
      const name = typeof expOrName === 'string' ? expOrName : this.getBasename(expOrName)
      if (!name) return
      if (this.downloadedExports.indexOf(name) === -1) {
        this.downloadedExports.push(name)
        if (persist) await this.persistDownloadedMetas()
      }
    },
    clearUiTimeout() {
      if (this._uiTimeoutHandle) {
        clearTimeout(this._uiTimeoutHandle)
        this._uiTimeoutHandle = null
      }
    },
    async withUiTimeout(promise, ms) {
      // Returns { state: 'resolved'|'timeout'|'rejected', value?, error? }
      return new Promise(resolve => {
        let settled = false
        this.clearUiTimeout()
        this._uiTimeoutHandle = setTimeout(() => {
          if (!settled) {
            settled = true
            resolve({ state: 'timeout' })
          }
        }, ms)
        promise.then(v => {
          if (!settled) {
            settled = true
            this.clearUiTimeout()
            resolve({ state: 'resolved', value: v })
          }
        }).catch(err => {
          if (!settled) {
            settled = true
            this.clearUiTimeout()
            resolve({ state: 'rejected', error: err })
          }
        })
      })
    },
    startPolling() {
      if (this.polling) return
      this.polling = true
      this.pollAttempts = 0
      const tick = async () => {
        if (!this.dialog) { this.stopPolling(); return }
        this.pollAttempts++
        await this.fetchExports()
        // stop if we detect new file
        const hasNew = (this.exports || []).some(e => {
          const fn = e && (e.filename || e.key || '')
          return (this.lastRequestedPrefix && fn && fn.startsWith(this.lastRequestedPrefix))
        }) || ((this.exports || []).length > this.baselineExportsCount)
        if (hasNew) {
          this.stopPolling()
          this.longRunning = false
          EventBus.$emit('show-snackbar', { text: "Votre export est prêt.", color: 'green', timeout: 4000 })
          return
        }
        if (this.pollAttempts >= this.pollMaxAttempts) {
          // give up softly
          this.stopPolling()
          EventBus.$emit('show-snackbar', { text: "La génération de l'export prend plus de temps que prévu. Vous pouvez laisser cette fenêtre ouverte ou revenir plus tard.", color: 'info', timeout: 6000 })
          return
        }
        this._pollHandle = setTimeout(tick, this.pollIntervalMs)
      }
      this._pollHandle = setTimeout(tick, this.pollIntervalMs)
    },
    stopPolling() {
      this.polling = false
      this.pollAttempts = 0
      if (this._pollHandle) {
        clearTimeout(this._pollHandle)
        this._pollHandle = null
      }
    },
    async submitExport() {
      // Trigger validation only on submit
      this.prefixValidateNow = true
      await this.$nextTick()
      if (!(this.$refs.form && this.$refs.form.validate())) return

      this.loading = true
      this.noData = false
      this.longRunning = false
      this.baselineExportsCount = (this.exports || []).length
      this.lastRequestedPrefix = this.prefix
      try {
        const user = AuthLogic.user()
        const payload = {
          user_uuid: user.uuid,
          prefix: this.prefix,
          unit_ids: (GlobalFiltersStore.selectedUnits || []).map(u => u.id || u),
          rating_channel_id: GlobalFiltersStore.selectedChannels || [],
          date_start: GlobalFiltersStore.startDate,
          date_end: GlobalFiltersStore.endDate
        }

        const res = await this.withUiTimeout(AlixLogic.createExport(payload), this.uiTimeoutMs)
        if (res.state === 'resolved') {
          EventBus.$emit('show-snackbar', { text : "Export demandé, vous pourrez télécharger votre fichier dans un instant.", color: 'green', timeout: 5000 })
          this.$emit('requested')
          await this.fetchExports()
        } else if (res.state === 'timeout') {
          // Inform user and begin background polling
          this.longRunning = true
          EventBus.$emit('show-snackbar', { text : "La demande d'export est en cours. Nous vous avertirons dès qu'elle sera prête.", color: 'info', timeout: 5000 })
          // release button loading so UI isn't blocked
          this.loading = false
          this.startPolling()
          return
        } else if (res.state === 'rejected') {
          const e = res.error
          const msg = (e && (e.message || e.msg)) || (e && e.response && (e.response.data && (e.response.data.message || e.response.data.error))) || ''
          const text = this.$t('components.export_dialog.request_export_failed') + (msg ? `: ${msg}` : '')
          EventBus.$emit('show-snackbar', { text, color: 'red', timeout: 5000 })
          this.noData = true
        }
      } catch (e) {
        // Fallback catch (should rarely hit due to withUiTimeout design)
        const msg = (e && (e.message || e.msg)) || (e && e.response && (e.response.data && (e.response.data.message || e.response.data.error))) || ''
        const text = this.$t('components.export_dialog.request_export_failed') + (msg ? `: ${msg}` : '')
        EventBus.$emit('show-snackbar', { text, color: 'red', timeout: 5000 })
        this.noData = true
      } finally {
        // Only reset loading if not long-running background
        if (!this.longRunning) this.loading = false
        this.prefix = ''
        // Reset validation state after attempt
        this.$refs.form && this.$refs.form.resetValidation && this.$refs.form.resetValidation()
        this.prefixValidateNow = false
      }
    },
    async download(exp) {
      try {
        // If the export has a direct URL, use it
        if (exp.url) {
          const w = window.open(exp.url, '_blank');
          // Optimistically mark as downloaded
          const name = this.getBasename(exp)
          this.markAsDownloaded(name)
          if (!w) {
            // popup blocked or similar; still persist state
          }
          return;
        }

        // Otherwise, stream the file via API (send only basename to backend)
        const reqName = (exp.key || exp.filename || '').split('/').pop();
        const res = await AlixLogic.downloadExport(reqName);

        // Extract filename from Content-Disposition header if available
        let filename = exp.key || exp.filename;
        const contentDisposition = res.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?/);
          if (fileNameMatch && fileNameMatch[1]) {
            filename = fileNameMatch[1];
          }
        }

        // Create Blob with correct Content-Type
        const blob = new Blob([res.data], {
          type: res.headers['content-type'] || 'application/octet-stream'
        });

        // Create download link and trigger download
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        // Mark as downloaded and persist
        await this.markAsDownloaded(reqName)
      } catch (e) {
        console.error('Download failed:', e);
        const msg = (e && (e.message || e.msg)) || (e && e.response && (e.response.data && (e.response.data.message || e.response.data.error))) || ''
        const text = this.$t('components.export_dialog.download_failed') + (msg ? `: ${msg}` : '')
        EventBus.$emit('show-snackbar', { text, color: 'red', timeout: 5000 })
      }
    }
  }
}
</script>


<style scoped>
.v-list-item--active {
  background-color: rgba(72, 159, 187, 0.24); /* 24% opacity blue */
  color: #489FBB;
}
</style>
